// src/pages/Dashboard/Admin/Inventaire/hooks/useInventaireDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllInventaire,
  addInventaire,
  updateInventaire,
  deleteInventaire,
} from "../../../../../services/inventaireServices";

import { uploadInventairePhoto } from "../../../../../services/inventairePhotoServices";

import {
  buildInventairePayload,
  calculateInventaireStats,
  filterInventaire,
  getInventaireCategories,
  sortInventaire,
} from "../utils/inventaireDashboardUtils";

const defaultFilters = {
  query: "",
  category: "",
  stockStatus: "",
  sort: "name_asc",
};

export function useInventaireDashboard() {
  const [inventaire, setInventaire] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [currentInventaire, setCurrentInventaire] = useState(null);

  const fetchInventaire = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getAllInventaire();
      const rows = Array.isArray(response) ? response : [];

      setInventaire(sortInventaire(rows, "name_asc"));
    } catch (error) {
      console.error("Erreur chargement inventaire :", error);
      toast.error("Erreur lors du chargement de l’inventaire.");
      setInventaire([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventaire();
  }, [fetchInventaire]);

  const categoryOptions = useMemo(() => {
    return getInventaireCategories(inventaire);
  }, [inventaire]);

  const filteredInventaire = useMemo(() => {
    return filterInventaire(inventaire, filters);
  }, [inventaire, filters]);

  const stats = useMemo(() => {
    return calculateInventaireStats(inventaire, filteredInventaire);
  }, [inventaire, filteredInventaire]);

  const openAddDrawer = () => {
    setAddDrawerOpen(true);
  };

  const closeAddDrawer = () => {
    if (saving) return;
    setAddDrawerOpen(false);
  };

  const openEditDrawer = (item) => {
    setCurrentInventaire(item);
    setEditDrawerOpen(true);
  };

  const closeEditDrawer = () => {
    if (saving) return;
    setCurrentInventaire(null);
    setEditDrawerOpen(false);
  };

  const handleCreateInventaire = async (formData) => {
    try {
      setSaving(true);

      const payload = buildInventairePayload(formData);
      await addInventaire(payload);

      toast.success("Objet ajouté à l’inventaire.");
      setAddDrawerOpen(false);

      await fetchInventaire();
    } catch (error) {
      console.error("Erreur création inventaire :", error);
      toast.error("Erreur lors de l’ajout de l’objet.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInventaire = async (id, formData) => {
    if (!id) {
      toast.error("Objet introuvable.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildInventairePayload(formData);
      await updateInventaire(id, payload);

      toast.success("Objet mis à jour.");
      setEditDrawerOpen(false);
      setCurrentInventaire(null);

      await fetchInventaire();
    } catch (error) {
      console.error("Erreur mise à jour inventaire :", error);
      toast.error("Erreur lors de la mise à jour de l’objet.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInventaire = async (itemOrId) => {
    const id = typeof itemOrId === "object" ? itemOrId?.id : itemOrId;

    if (!id) {
      toast.error("Objet introuvable.");
      return;
    }

    if (!window.confirm("Supprimer cet objet de l’inventaire ?")) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteInventaire(id);

      toast.success("Objet supprimé.");

      setInventaire((prev) => prev.filter((item) => item.id !== id));

      if (currentInventaire?.id === id) {
        setCurrentInventaire(null);
        setEditDrawerOpen(false);
      }
    } catch (error) {
      console.error("Erreur suppression inventaire :", error);
      toast.error("Erreur lors de la suppression de l’objet.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploadInventairePhoto = async ({
    inventaireId,
    file,
    format,
    onUploadProgress,
  }) => {
    if (!inventaireId) {
      toast.error("Objet introuvable.");
      return null;
    }

    if (!file) {
      toast.error("Veuillez sélectionner une image.");
      return null;
    }

    try {
      setSaving(true);

      const response = await uploadInventairePhoto(inventaireId, file, {
        format,
        onUploadProgress,
      });

      const photoUrl =
        response?.photo_url ||
        response?.inventaire?.photo_url ||
        response?.data?.photo_url ||
        "";

      if (photoUrl) {
        setInventaire((prev) =>
          prev.map((item) =>
            item.id === inventaireId ? { ...item, photo_url: photoUrl } : item
          )
        );

        setCurrentInventaire((prev) =>
          prev?.id === inventaireId ? { ...prev, photo_url: photoUrl } : prev
        );
      }

      toast.success("Image téléversée et optimisée.");

      await fetchInventaire();

      return photoUrl;
    } catch (error) {
      console.error("Erreur upload photo inventaire :", error);
      toast.error("Erreur lors du téléversement de l’image.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const refreshInventaire = useCallback(async () => {
    await fetchInventaire();
  }, [fetchInventaire]);

  return {
    inventaire,
    filteredInventaire,
    categoryOptions,
    stats,

    filters,
    setFilters,

    loading,
    saving,
    deletingId,

    addDrawerOpen,
    editDrawerOpen,
    currentInventaire,

    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,

    handleCreateInventaire,
    handleUpdateInventaire,
    handleDeleteInventaire,
    handleUploadInventairePhoto,
    refreshInventaire,
  };
}